import {
  Autocomplete,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField
} from '@mui/material';
import { deepEqual } from 'fast-equals';
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { ImageUpload, Loader, StatesDropdown } from '../../../components';
import { AccordionList } from '../../../components/accordion/simple-accordion-list';
import {
  getLenderBuilds,
  getPeopleLenderImage,
  getPeopleLenderTerritories,
  getPerson
} from '../../../fetch';
import { getAdminLookupTerritories, getCommonLookupLenders } from '../../../fetch/lookups';
import { formatInputPhoneNumber } from '../../../helpers';
import { IDropdownResponse, IPeopleBuilds, IPeopleDetail } from '../../../models';

export const LenderProcessorFields = ({
  setFieldValue,
  isFormDisabled,
  values,
  initialValues,
  handleBlur,
  touched,
  errors,
  currentExternalPersonId,
  showContactInfo = true
}) => {
  const [territoryValues, setTerritoryValues] = useState<IDropdownResponse[]>([]);
  const [inputTerritoryValue, setInputTerritoryValue] = useState('');

  const [lenderValues, setLenderValues] = useState<any[]>([]);
  const [inputLenderValue, setInputLenderValue] = useState('');

  const { isLoading: isLoadingTerritories, data: territoriesData } = useQuery<
    IDropdownResponse[],
    Error
  >(['getTerritories'], () => getAdminLookupTerritories(), {
    onSuccess: d => {
      const options = d.filter(record => values?.lenderTerritoryIds?.includes(+record.value));
      setTerritoryValues(options);
    }
  });

  const territories = useMemo(() => territoriesData ?? [], [territoriesData]);

  const { isLoading: isLoadingPersonalImage, data: personalImage } = useQuery<any, Error>(
    ['lender-personal-image', currentExternalPersonId],
    () => getPeopleLenderImage(currentExternalPersonId, 'personal'),
    {
      enabled: !!currentExternalPersonId
    }
  );
  const { isLoading: isLoadingLogoImage, data: logoImage } = useQuery<any, Error>(
    ['lender-logo-image', currentExternalPersonId],
    () => getPeopleLenderImage(currentExternalPersonId, 'logo'),
    {
      enabled: !!currentExternalPersonId
    }
  );

  const { isLoading: isLoadingLenders, data: lenders } = useQuery<IDropdownResponse[], Error>(
    ['select-lender'],
    () => getCommonLookupLenders(),
    {
      onSuccess: d => {
        if (values.assignedLenders) {
          // We loop over the values instead of the returned data to ensure that the order of the selected lenders is retained.
          // This is important because the order of the selected lenders is the default values for the fields.
          const retainedOrder = values.assignedLenders.map(
            lenderId => d.find(l => +l.value === lenderId) ?? null
          );
          setLenderValues(retainedOrder.filter(Boolean));
        }
      }
    }
  );

  const updateFields = (data: IPeopleDetail) => {
    setFieldValue('workplace', data.workplace ?? '');
    setFieldValue('address', data.address ?? '');
    setFieldValue('address2', data.address2 ?? '');
    setFieldValue('city', data.city ?? '');
    setFieldValue('state', data.state ?? '');
    setFieldValue('zip', data.zip ?? '');
    setFieldValue('lenderStatus', data.lenderStatus ?? '');
    setFieldValue('lenderTerritoryIds', data.lenderTerritoryIds ?? '');
    setTerritoryValues(
      territories.filter(territory => data.lenderTerritoryIds?.includes(+territory.value))
    );
  };

  const { isLoading: isAssociatedLenderDataLoading } = useQuery<IPeopleDetail | undefined, Error>(
    ['lenderData', values.assignedLenders?.[0]],
    () => getPerson(values.assignedLenders?.[0]),
    {
      enabled:
        !!values.assignedLenders?.[0] &&
        !deepEqual(initialValues.assignedLenders?.[0], values?.assignedLenders?.[0]),
      onSuccess(data) {
        // set all the values for the lender

        updateFields(data);
      }
    }
  );

  const { isLoading: isLoadingBuilds, data: buildData } = useQuery<
    IPeopleBuilds[] | undefined,
    Error
  >(['lenderBuilds', currentExternalPersonId], () => getLenderBuilds(currentExternalPersonId), {
    enabled: !!currentExternalPersonId
  });

  const { isLoading: isLoadingSelectedTerritories, data: selectedTerritoriesData } = useQuery<
    number[] | undefined,
    Error
  >(
    ['selectedTerritories', values?.assignedLenders],
    () => getPeopleLenderTerritories(values?.assignedLenders),
    {
      enabled: values?.assignedLenders?.length > 0
    }
  );

  useEffect(() => {
    if (deepEqual(initialValues.assignedLenders, values?.assignedLenders)) {
      return;
    }
    const newTerritoryValues = territories?.filter(territory => {
      return selectedTerritoriesData?.includes(+territory.value);
    });
    setTerritoryValues(newTerritoryValues);
    setFieldValue('lenderTerritoryIds', selectedTerritoriesData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTerritoriesData, territories, values?.assignedLenders]);

  const builds = useMemo(() => buildData ?? [], [buildData]);

  return (
    <>
      <Grid item xs={12}>
        <Autocomplete
          multiple
          id="assignedLenders"
          value={lenderValues}
          onChange={(event, newValue: IDropdownResponse[]) => {
            setLenderValues(newValue);
            setFieldValue(
              `assignedLenders`,
              newValue.map(val => val.value)
            );
          }}
          inputValue={inputLenderValue}
          onInputChange={(event, newInputValue) => {
            setInputLenderValue(newInputValue);
          }}
          options={isLoadingLenders ? [] : lenders}
          getOptionLabel={option => option?.description}
          filterSelectedOptions
          renderOption={(props, option: IDropdownResponse) => {
            return (
              <li {...props} key={`${option.value}`}>
                {option.description}
              </li>
            );
          }}
          renderInput={params => (
            <TextField
              {...params}
              size="small"
              disabled={isLoadingTerritories || isAssociatedLenderDataLoading}
              label={isLoadingTerritories ? 'Loading...' : 'Lenders'}
              placeholder="Add Lenders"
            />
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <Autocomplete
          multiple
          id="territores"
          value={territoryValues}
          onChange={(event, newValue: IDropdownResponse[]) => {
            setTerritoryValues(newValue);
            setFieldValue(
              `lenderTerritoryIds`,
              newValue.map(val => val.value)
            );
          }}
          inputValue={inputTerritoryValue}
          onInputChange={(event, newInputValue) => {
            setInputTerritoryValue(newInputValue);
          }}
          options={territories}
          getOptionLabel={option => option.description}
          filterSelectedOptions
          renderOption={(props, option: IDropdownResponse) => {
            return (
              <li {...props} key={`${option.value}`}>
                {option.description}
              </li>
            );
          }}
          renderInput={params => (
            <TextField
              {...params}
              size="small"
              disabled={
                isLoadingTerritories ||
                isAssociatedLenderDataLoading ||
                isLoadingSelectedTerritories
              }
              label={isLoadingTerritories ? 'Loading...' : 'Territories'}
              placeholder="Add Territory"
            />
          )}
        />
      </Grid>
      {showContactInfo && (
        <>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              variant="outlined"
              autoComplete="nope"
              label="First Name"
              name="firstName"
              value={values.firstName}
              disabled={isFormDisabled || isAssociatedLenderDataLoading}
              onBlur={handleBlur}
              size="small"
              inputProps={{
                'data-lpignore': 'true'
              }}
              error={touched.firstName && errors && errors.firstName ? true : false}
              helperText={touched.firstName && errors && errors.firstName}
              onChange={e => setFieldValue('firstName', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              variant="outlined"
              autoComplete="nope"
              label="Last Name"
              name="lastName"
              inputProps={{
                'data-lpignore': 'true'
              }}
              value={values.lastName}
              disabled={isFormDisabled || isAssociatedLenderDataLoading}
              onBlur={handleBlur}
              size="small"
              error={touched.lastName && errors && errors.lastName ? true : false}
              helperText={touched.lastName && errors && errors.lastName}
              onChange={e => setFieldValue('lastName', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              onChange={e => setFieldValue('email', e.target.value)}
              error={touched.email && errors && errors.email ? true : false}
              helperText={touched.email && errors && errors.email}
              fullWidth={true}
              id="email"
              label="Email (Username)"
              size="small"
              name="email"
              required={true}
              autoComplete="off"
              onBlur={handleBlur}
              value={values.email}
              disabled={isFormDisabled || isAssociatedLenderDataLoading}
              inputProps={{
                'data-lpignore': true // Last Pass disable auto-fill
              }}
            />
          </Grid>
        </>
      )}
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          variant="outlined"
          autoComplete="nope"
          label="Bank Name"
          name="workplace"
          inputProps={{
            'data-lpignore': 'true'
          }}
          value={values.workplace}
          disabled={isFormDisabled || isAssociatedLenderDataLoading}
          onBlur={handleBlur}
          size="small"
          error={touched.workplace && errors && errors.workplace ? true : false}
          helperText={touched.workplace && errors && errors.workplace}
          onChange={e => setFieldValue('workplace', e.target.value)}
        />
      </Grid>
      {showContactInfo && (
        <>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              variant="outlined"
              autoComplete="nope"
              label="Phone Number"
              name="phoneNumber"
              value={values.phoneNumber}
              disabled={isFormDisabled || isAssociatedLenderDataLoading}
              onBlur={handleBlur}
              size="small"
              onChange={e => {
                setFieldValue('phoneNumber', formatInputPhoneNumber(e.target.value));
              }}
              error={Boolean(touched.phoneNumber && errors.phoneNumber)}
              helperText={touched.phoneNumber && errors.phoneNumber}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              variant="outlined"
              autoComplete="nope"
              label="Cell Phone Number"
              name="phone"
              value={values.cellPhoneNumber}
              disabled={isFormDisabled || isAssociatedLenderDataLoading}
              onBlur={handleBlur}
              size="small"
              onChange={e => {
                setFieldValue('cellPhoneNumber', formatInputPhoneNumber(e.target.value));
              }}
              error={Boolean(touched.cellPhoneNumber && errors.cellPhoneNumber)}
              helperText={touched.cellPhoneNumber && errors.cellPhoneNumber}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              variant="outlined"
              autoComplete="nope"
              label="Fax"
              name="phone"
              value={values.fax}
              disabled={isFormDisabled || isAssociatedLenderDataLoading}
              onBlur={handleBlur}
              size="small"
              onChange={e => {
                setFieldValue('fax', formatInputPhoneNumber(e.target.value));
              }}
              error={Boolean(touched.fax && errors.fax)}
              helperText={touched.fax && errors.fax}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              variant="outlined"
              autoComplete="nope"
              label="Address Line 1"
              name="address"
              inputProps={{
                'data-lpignore': 'true'
              }}
              value={values.address}
              disabled={isFormDisabled || isAssociatedLenderDataLoading}
              onBlur={handleBlur}
              size="small"
              error={touched.address && errors && errors.address ? true : false}
              helperText={touched.address && errors && errors.address}
              onChange={e => setFieldValue('address', e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              variant="outlined"
              autoComplete="nope"
              label="Address Line 2"
              name="address2"
              inputProps={{
                'data-lpignore': 'true'
              }}
              value={values.address2}
              disabled={isFormDisabled || isAssociatedLenderDataLoading}
              onBlur={handleBlur}
              size="small"
              error={touched.address2 && errors && errors.address2 ? true : false}
              helperText={touched.address2 && errors && errors.address2}
              onChange={e => setFieldValue('address2', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              autoComplete="nope"
              label="City"
              name="city"
              inputProps={{
                'data-lpignore': 'true'
              }}
              value={values.city}
              disabled={isFormDisabled || isAssociatedLenderDataLoading}
              onBlur={handleBlur}
              size="small"
              error={touched.city && errors && errors.city ? true : false}
              helperText={touched.city && errors && errors.city}
              onChange={e => setFieldValue('city', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatesDropdown
              value={values.state}
              isDisabled={isFormDisabled || isAssociatedLenderDataLoading}
              handleChange={value => {
                setFieldValue('state', value as string);
              }}
              isAdminPortal={true}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              autoComplete="nope"
              label="ZIP"
              name="zip"
              inputProps={{
                'data-lpignore': 'true'
              }}
              value={values.zip}
              disabled={isFormDisabled || isAssociatedLenderDataLoading}
              onBlur={handleBlur}
              size="small"
              error={touched.zip && errors && errors.zip ? true : false}
              helperText={touched.zip && errors && errors.zip}
              onChange={e => setFieldValue('zip', e.target.value)}
            />
          </Grid>
        </>
      )}
      <Grid item xs={12}>
        <FormControl
          component="fieldset"
          disabled={isFormDisabled || isAssociatedLenderDataLoading}
        >
          <FormLabel required>Lender Status</FormLabel>
          <RadioGroup
            defaultValue="NotApproved"
            row
            aria-label="lenderStatus"
            name="lenderStatus"
            value={values.lenderStatus}
            onChange={(e, value) => setFieldValue('lenderStatus', e.target.value)}
          >
            <FormControlLabel value="Approved" control={<Radio />} label="Approved" />
            <FormControlLabel value="NotApproved" control={<Radio />} label="Not Approved" />
          </RadioGroup>
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControl>
          <FormLabel>Personal Photo</FormLabel>
          <ImageUpload
            showLabel={false}
            imageKey={`personal`}
            disabled={isLoadingPersonalImage || isAssociatedLenderDataLoading}
            handleFileChange={(val, file) => {
              setFieldValue('uploadPersonalFile', file);
            }}
            canOverride
            canRemoveImage={false}
            image={
              currentExternalPersonId && !!personalImage
                ? `${window.location.origin}/api/people/lenders/${currentExternalPersonId}/photos/personal`
                : null
            }
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControl>
          <FormLabel>Company Logo Image</FormLabel>
          <ImageUpload
            imageKey={`logo`}
            showLabel={false}
            disabled={isLoadingLogoImage || isAssociatedLenderDataLoading}
            handleFileChange={(val, file) => {
              setFieldValue('uploadLogoFile', file);
            }}
            canOverride
            canRemoveImage={false}
            image={
              currentExternalPersonId && !!logoImage
                ? `${window.location.origin}/api/people/lenders/${currentExternalPersonId}/photos/logo`
                : null
            }
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        {isLoadingBuilds && <Loader type={'inline'} position="centered" />}
        {!isLoadingBuilds && builds?.length > 0 && (
          <AccordionList
            pluralItemName={'Builds'}
            title={`Builds (${Array.from(new Set(builds)).length})`}
            items={builds.map(build => build.neighborhoodDisplay)}
          />
        )}
      </Grid>
    </>
  );
};
